import type { SKUStoreStatusBadgeTone } from "@/features/SKUStore/components/Status/SKUStoreStatusBadges";
import type { SkuStoreStockState } from "@/features/SKUStore/utils/skuStoreAvailability";
import type {
  InventorySummary,
  ProductFlags,
  VariantSchema,
} from "@/types/ingestedProduct";

import { resolveStockState } from "@/features/SKUStore/utils/skuStoreAvailability";

/**
 * One chip in a card's status row, as data. Rows show chips in array order;
 * when they do not all fit on one line, the highest `priority` numbers fold
 * into a "+N" chip first, whose tooltip lists their labels.
 */
export type SKUStoreStatusBadgeItem = {
  key: string;
  label: string;
  tone: SKUStoreStatusBadgeTone;
  /** Hover detail (e.g. affected variant counts). */
  tooltip?: string;
  /** Makes the chip an actionable button (e.g. deep-link to a tab). */
  onClick?: () => void;
  /** Lower stays visible longer. */
  priority: number;
  /** Never folded away (e.g. the chip that opens the variant list). */
  pinned?: boolean;
};

/** Folding order shared by every card: actionable stock first, inventory details last. */
export const STATUS_BADGE_PRIORITY = {
  stock: 1,
  priceDrop: 2,
  new: 3,
  quantity: 4,
  inventory: 5,
} as const;

/**
 * The chips that do not fit in `available` pixels on one line, in row order.
 *
 * Pinned chips always show. The rest are admitted by priority (ties by row
 * position) while they, the gaps between chips, and a "+N" chip for whatever
 * is left still fit. Admission stops at the first chip that does not fit, so
 * a smaller, lower-priority chip never shows while a higher one is hidden.
 * An unmeasured row (`available` of 0) hides nothing.
 */
export function fitStatusBadges(
  items: SKUStoreStatusBadgeItem[],
  widths: readonly number[],
  {
    available,
    gap,
    overflowWidth,
  }: { available: number; gap: number; overflowWidth: number },
): SKUStoreStatusBadgeItem[] {
  if (available <= 0 || items.length === 0) {
    return [];
  }
  const widthOf = (index: number) => widths[index] ?? 0;
  const total =
    items.reduce((sum, _item, index) => sum + widthOf(index), 0) +
    gap * (items.length - 1);
  if (total <= available) {
    return [];
  }

  let used = 0;
  let shown = 0;
  const admit = (index: number) => {
    used += (shown > 0 ? gap : 0) + widthOf(index);
    shown += 1;
  };
  items.forEach((item, index) => {
    if (item.pinned) admit(index);
  });

  const candidates = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => !item.pinned)
    .sort((a, b) => a.item.priority - b.item.priority || a.index - b.index);
  const admitted = new Set<string>();
  for (const [position, { item, index }] of candidates.entries()) {
    const leftOver = candidates.length - position - 1;
    const needed =
      used +
      (shown > 0 ? gap : 0) +
      widthOf(index) +
      (leftOver > 0 ? gap + overflowWidth : 0);
    if (needed > available) {
      break;
    }
    admit(index);
    admitted.add(item.key);
  }

  return items.filter((item) => !item.pinned && !admitted.has(item.key));
}

/** One decimal at most: 12.5 → "12.5", 10 → "10". */
function formatPercent(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/**
 * Low stock / New / Price drop chips for a product card, driven only by the
 * server-derived {@link ProductFlags} and {@link InventorySummary} (thresholds
 * live in ProductCatalog).
 */
export function productStatusBadgeItems({
  flags,
  inventory,
}: {
  /** Drives the New and Price drop chips. */
  flags: ProductFlags | null | undefined;
  /** Drives the Low stock chip and its "{n} of {m} variants low" tooltip. */
  inventory?: InventorySummary | null;
}): SKUStoreStatusBadgeItem[] {
  const items: SKUStoreStatusBadgeItem[] = [];

  if (inventory != null && inventory.low_stock_variants > 0) {
    items.push({
      key: "low_stock",
      label: "Low stock",
      tone: "warning",
      tooltip: `${String(inventory.low_stock_variants)} of ${String(
        inventory.sellable_variants,
      )} variants low`,
      priority: STATUS_BADGE_PRIORITY.stock,
    });
  }
  if (flags?.new_since != null) {
    items.push({
      key: "new",
      label: "New",
      tone: "info",
      priority: STATUS_BADGE_PRIORITY.new,
    });
  }
  const drop = flags?.price_drop;
  if (drop != null) {
    items.push({
      key: "price_drop",
      label: "Price drop",
      tone: "success",
      tooltip: `-${formatPercent(drop.max_drop_percent)}% on ${String(
        drop.variant_count,
      )} ${drop.variant_count === 1 ? "variant" : "variants"}`,
      priority: STATUS_BADGE_PRIORITY.priceDrop,
    });
  }
  return items;
}

const STOCK_STATE_BADGES: Partial<
  Record<SkuStoreStockState, { key: string; label: string }>
> = {
  unavailable: { key: "unavailable", label: "Unavailable" },
  out_of_stock: { key: "out_of_stock", label: "Out of stock" },
};

/**
 * Unavailable / Out of stock / inventory-detail / Price drop chips for one
 * variant row or card. Low stock is not derived here: its threshold is
 * server-owned and only exists at product level.
 */
export function variantStatusBadgeItems(
  variant: VariantSchema,
): SKUStoreStatusBadgeItem[] {
  const items: SKUStoreStatusBadgeItem[] = [];
  const { availability } = variant;

  const stockBadge = STOCK_STATE_BADGES[resolveStockState(availability)];
  if (stockBadge) {
    items.push({
      ...stockBadge,
      tone: "error",
      priority: STATUS_BADGE_PRIORITY.stock,
    });
  }
  // Only explicit values: unknown (null/missing) tracking or policy shows nothing.
  if (availability.inventory_tracked === false) {
    items.push({
      key: "inventory_not_tracked",
      label: "Inventory not tracked",
      tone: "neutral",
      priority: STATUS_BADGE_PRIORITY.inventory,
    });
  } else if (
    availability.inventory_tracked === true &&
    availability.inventory_policy === "continue"
  ) {
    items.push({
      key: "continue_selling",
      label: "Continue selling when out of stock",
      tone: "neutral",
      priority: STATUS_BADGE_PRIORITY.inventory,
    });
  }
  // Server-derived (recency window and recovery applied): gate on it alone,
  // never re-derive from the raw drop history.
  if (variant.price_drop_active === true) {
    const pct = variant.last_price_drop_percent;
    items.push({
      key: "price_drop",
      label: pct != null ? `Price drop -${formatPercent(pct)}%` : "Price drop",
      tone: "success",
      tooltip: variant.price_before_last_drop
        ? `Was ${variant.price_before_last_drop}`
        : undefined,
      priority: STATUS_BADGE_PRIORITY.priceDrop,
    });
  }
  return items;
}
